import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useEffect, useState } from 'react';

type Role = {
  id: number;
  serial_number: number;
  latka_code: string;
  latka_name: string;
  latka_type: string;
  unit: string;
  stock_qty: number;
  received_at: number | null;
  /** 0 = role, 1 = látka přeložená na půl. */
  folded: number;
};

type Objednana = {
  id: number;
  serial_number: number;
  latka_code: string;
  latka_name: string;
  latka_type: string;
  unit: string;
  quantity: number;
  order_id: number;
  supplier: string;
  expected_at: number | null;
  created_at: number;
};

type Data = { latka: string; on_stock: Role[]; ordered: Objednana[] };

/** Metry s jedním desetinným místem, bez zbytečné nuly. */
function fmtQty(q: number, unit: string): string {
  const n = Math.round(q * 10) / 10;
  return `${String(n).replace('.', ',')} ${unit || 'm'}`;
}

/** Unix ms → „5.8.2026". */
function fmtDate(ms: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

/**
 * Materiál produktu — role látky na skladě a to, co je u dodavatele objednané,
 * ale ještě nedorazilo. Střihárna se tak nemusí proklikávat do skladu látek.
 */
export function MaterialModal({
  productCode,
  which,
  onClose,
}: {
  productCode: string;
  /** 1-based pořadí látky — u produktů se dvěma látkami (viz "Materiál 1"/"Materiál 2"). */
  which?: number;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const whichParam = which ? `&which=${which}` : '';
    apiFetch<Data>(
      `/api/vyroba/striharna/material?code=${encodeURIComponent(productCode)}${whichParam}`,
    )
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : 'Nepodařilo se načíst materiál');
      });
    return () => {
      cancelled = true;
    };
  }, [productCode, which]);

  const celkem = (data?.on_stock ?? []).reduce((s, r) => s + r.stock_qty, 0);
  const unit = data?.on_stock[0]?.unit ?? data?.ordered[0]?.unit ?? 'm';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 cursor-default"
      />
      <div className="relative w-full max-w-lg my-8 bg-surface rounded-2xl shadow-modal reveal">
        <div className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-border">
          <h2 className="text-lg font-bold text-text">
            Materiál{which ? ` ${which}` : ''}
            {data?.latka ? `: ${data.latka}` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="text-text-muted hover:text-text text-2xl leading-none cursor-pointer"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4">
          {err && <p className="text-sm text-danger">{err}</p>}
          {!err && data === null && <p className="text-sm text-text-muted">Načítám…</p>}

          {data && (
            <>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <h3 className="text-sm font-bold text-text">Role na skladě</h3>
                <span className="text-sm text-text-secondary">
                  celkem <strong className="font-semibold text-text">{fmtQty(celkem, unit)}</strong>
                </span>
              </div>
              {data.on_stock.length > 0 && (
                <ul className="mb-5 divide-y divide-border-light">
                  {data.on_stock.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-3 py-1.5">
                      <span className="text-[15px] text-text">
                        <span className="font-semibold">č. {r.serial_number}</span>
                        <span className="text-text-muted"> · {r.folded ? 'Složené' : 'Role'}</span>
                      </span>
                      <span className="text-[15px] font-semibold text-action-dark whitespace-nowrap">
                        {fmtQty(r.stock_qty, r.unit)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="text-sm font-bold text-text mb-2">Objednáno</h3>
              {data.ordered.length > 0 && (
                <ul className="divide-y divide-border-light">
                  {data.ordered.map((o) => (
                    <li key={o.id} className="flex items-center justify-between gap-3 py-1.5">
                      <span className="text-[15px] text-text">
                        🚚 <span className="font-semibold">č. {o.serial_number}</span>
                        {o.supplier && <span className="text-text-muted"> · {o.supplier}</span>}
                      </span>
                      <span className="text-[15px] text-text-secondary whitespace-nowrap">
                        {fmtQty(o.quantity, o.unit)} · {fmtDate(o.expected_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
